import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';

import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';
import { formatCalendarDateInTimeZone, getUserTimezone, toLocalCalendarYmd } from '../shared/utils/date.util';
import type { RewardSettings, RewardSettingsApiResponse } from '../employee/models/employee.models';
import type {
  CreateRewardRuleRequest,
  UpdateRewardRuleRequest,
  OfferedRewardsResponse,
  RewardRule,
  AdminRewardsSummaryFilters,
  AdminRewardsSummaryResponse,
  RewardsHistoryFilters,
  RewardsHistoryResponse,
} from '../rewards/models/rewards-v2.models';

export type {
  AppConfig,
  CreateRewardRuleRequest,
  UpdateRewardRuleRequest,
  OfferedRewardRule,
  OfferedRewardsResponse,
  RewardRule,
  RewardsHistoryColumn,
  RewardsHistoryFilters,
  RewardsHistoryItemV2,
  RewardsHistoryResponse,
} from '../rewards/models/rewards-v2.models';

// Re-export for backward compatibility (e.g. rewards.service.spec)
export type { RewardSettings, RewardSettingsApiResponse };

// Reward Types
export type RewardType = 'single_wash' | 'membership';
export type RewardStatus = 'earned' | 'pending' | 'redeemed';

export interface RewardRecord {
  id: string;
  employee_id: string;
  reward_type: RewardType;
  count_at_time: number;
  reward_value: number;
  status: RewardStatus;
  created_at: string;
  updated_at: string;
  user_id: string;
}

export interface RewardsPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface RewardsApiResponse {
  data: RewardRecord[];
  pagination: RewardsPagination;
}

// New API response structure for rewards history
export interface RewardPointsRecord {
  employee_id: string;
  single_wash_points: string;
  single_wash_count: string;
  membership_count: string;
  membership_points: string;
  total_points: string;
  membership_threshold_points?: string;
  date?: string;
}

export interface RewardsPointsApiResponse {
  data: RewardPointsRecord[];
  pagination: RewardsPagination;
}

export interface RewardsSummary {
  lifetimeRewards: number;
  singleWashProgress: {
    current: number;
    total: number;
    reward: string;
    wonToday: string;
  };
  membershipProgress: {
    reward: string;
    description: string;
    wonToday: string;
  };
}

export interface RewardsHistoryItem {
  date: string;
  singleWashReward: string;
  membershipReward: string;
  totalRewards: string;
  membership_threshold_points: string;
}

export interface RewardsFilters {
  page?: number;
  limit?: number;
  start_date?: string;
  end_date?: string;
  reward_type?: RewardType;
  status?: RewardStatus;
}

// Reward Settings interfaces (same as employee module) - now imported from employee.models, re-exported above

// Reward Statistics interfaces
export interface RewardStatistics {
  reward: {
    id: string;
    user_id: string;
    membership_reward: number;
    wash_count: number;
    threshold_reward: number;
    created_at: string;
    updated_at: string;
  };
  today: {
    subscription: number;
    washes: number;
  };
  total: {
    subscription: number;
    /** Current progress toward the next membership-threshold bonus. */
    membership_threshold_count?: number;
    /** Lifetime membership-threshold (e.g. “every N memberships”) rewards in dollars. */
    threshold_reward?: number;
    washes: number;
    /** Lifetime count of membership-threshold bonus events (not current-cycle progress). */
    totalwashcount: number;
  };
}

export interface RewardStatisticsApiResponse {
  status: boolean;
  status_code: number;
  message: string;
  data: RewardStatistics;
}

/**
 * Service for managing rewards data and API interactions
 * Provides observables for rewards, summary, and loading state
 */
@Injectable({
  providedIn: 'root'
})
export class RewardsService {
  private readonly rewardsSubject = new BehaviorSubject<RewardRecord[]>([]);
  public readonly rewards$ = this.rewardsSubject.asObservable();

  private readonly summarySubject = new BehaviorSubject<RewardsSummary | null>(null);
  public readonly summary$ = this.summarySubject.asObservable();

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  public readonly loading$ = this.loadingSubject.asObservable();

  constructor(private readonly apiService: ApiService) {}

  /**
   * Admin reward rules list (V2).
   */
  getRewardRules(isActive?: boolean): Observable<RewardRule[]> {
    const params: Record<string, boolean> = {};
    if (isActive !== undefined) {
      params['is_active'] = isActive;
    }

    return this.apiService.get<RewardRule[]>('employee/reward-rules', params).pipe(
      map((response: ApiResponse<RewardRule[]>) => {
        if (response.status) {
          return (response.data ?? []).map(rule => this.normalizeRewardRule(rule));
        }
        throw new Error(response.message || 'Failed to load reward rules');
      }),
      catchError(error => throwError(() => error))
    );
  }

  private normalizeRewardRule(rule: RewardRule): RewardRule {
    return {
      ...rule,
      amount: Number(rule.amount),
      items_sold: Number(rule.items_sold),
    };
  }

  /**
   * Create a reward rule (admin V2).
   */
  createRewardRule(body: CreateRewardRuleRequest): Observable<RewardRule> {
    return this.apiService.post<RewardRule>('employee/reward-rules', body).pipe(
      map((response: ApiResponse<RewardRule>) => {
        if (response.status && response.data) {
          return this.normalizeRewardRule(response.data);
        }
        throw new Error(response.message || 'Failed to create reward rule');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Update a reward rule (admin V2).
   */
  updateRewardRule(ruleId: string, body: UpdateRewardRuleRequest): Observable<RewardRule> {
    return this.apiService.put<RewardRule>(`employee/reward-rules/${ruleId}`, body).pipe(
      map((response: ApiResponse<RewardRule>) => {
        if (response.status && response.data) {
          return this.normalizeRewardRule(response.data);
        }
        throw new Error(response.message || 'Failed to update reward rule');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Deactivate a reward rule (admin V2).
   */
  deactivateRewardRule(ruleId: string): Observable<RewardRule> {
    return this.apiService.patch<RewardRule>(`employee/reward-rules/${ruleId}/deactivate`, {}).pipe(
      map((response: ApiResponse<RewardRule>) => {
        if (response.status && response.data) {
          return this.normalizeRewardRule(response.data);
        }
        throw new Error(response.message || 'Failed to deactivate reward rule');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Staff offered reward cards with lifetime earnings (V2).
   */
  getOfferedRewards(): Observable<OfferedRewardsResponse> {
    const timezone = getUserTimezone();
    return this.apiService.get<OfferedRewardsResponse>('employee/reward-rules/offered', { timezone }).pipe(
      map((response: ApiResponse<OfferedRewardsResponse>) => {
        if (response.status && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load offered rewards');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Staff rewards history with dynamic rule columns (V2).
   */
  getRewardsHistoryV2(filters: RewardsHistoryFilters = {}): Observable<RewardsHistoryResponse> {
    const params: Record<string, string | number> = {
      timezone: filters.timezone ?? getUserTimezone(),
    };
    if (filters.page != null) params['page'] = filters.page;
    if (filters.limit != null) params['limit'] = filters.limit;
    if (filters.start_date) params['start_date'] = filters.start_date;
    if (filters.end_date) params['end_date'] = filters.end_date;

    return this.apiService.get<RewardsHistoryResponse>('employee/rewards/history', params).pipe(
      map((response: ApiResponse<RewardsHistoryResponse>) => {
        if (response.status && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load rewards history');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Admin per-employee rewards earning summary with dynamic rule columns (V2).
   */
  getAdminRewardsEarningSummary(
    filters: AdminRewardsSummaryFilters = {}
  ): Observable<AdminRewardsSummaryResponse> {
    const params: Record<string, string | number> = {
      timezone: filters.timezone ?? getUserTimezone(),
    };
    if (filters.page != null) params['page'] = filters.page;
    if (filters.limit != null) params['limit'] = filters.limit;
    if (filters.search) params['search'] = filters.search;
    if (filters.start_date) params['start_date'] = filters.start_date;
    if (filters.end_date) params['end_date'] = filters.end_date;

    return this.apiService.get<AdminRewardsSummaryResponse>('employee/rewards/summary', params).pipe(
      map((response: ApiResponse<AdminRewardsSummaryResponse>) => {
        if (response.status && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load rewards summary');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Get rewards with pagination and filters
   * @param filters - Filter options for rewards query
   * @returns Observable of rewards API response
   */
  private buildRewardsParams(filters: RewardsFilters): Record<string, string | number> {
    const params: Record<string, string | number> = {
      page: filters.page ?? 1,
      limit: filters.limit ?? 10,
      timezone: getUserTimezone()
    };
    if (filters.start_date) params['start_date'] = filters.start_date;
    if (filters.end_date) params['end_date'] = filters.end_date;
    if (filters.reward_type) params['reward_type'] = filters.reward_type;
    if (filters.status) params['status'] = filters.status;
    return params;
  }

  /**
  * Fetches rewards from the API, updates internal subjects and manages loading state.
  * @example
  * etRewards({ status: 'active', page: 1 })
  * Observable<ApiResponse<RewardsApiResponse>>
  * @param {RewardsFilters} filters - Optional filters to apply when fetching rewards (default: {}).
  * @returns {Observable<ApiResponse<RewardsApiResponse>>} Observable that emits the API response containing rewards.
  **/
  getRewards(filters: RewardsFilters = {}): Observable<ApiResponse<RewardsApiResponse>> {
    this.loadingSubject.next(true);
    const params = this.buildRewardsParams(filters);

    return this.apiService.get<RewardsApiResponse>('attendant/me/rewards', params).pipe(
      tap((response) => {
        if (response.status && response.data) {
          this.rewardsSubject.next(response.data.data);
        }
      }),
      catchError(error => {
        return throwError(() => error);
      }),
      tap(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Get reward settings from API
   * @returns Observable of reward settings
   */
  getRewardSettings(): Observable<RewardSettings> {
    return this.apiService.get<RewardSettingsApiResponse>('employee/reward-settings').pipe(
      map((response: ApiResponse<RewardSettingsApiResponse>) => {
        if (response.status && response.data?.data) {
          return response.data.data;
        }
        throw new Error(response.message || 'Failed to load reward settings');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Get reward statistics from API
   * Used for statistics cards display
   * @returns Observable of reward statistics
   */
  getRewardStatistics(): Observable<RewardStatistics> {
    const timezone = getUserTimezone();
    return this.apiService.get<RewardStatistics>('employee/reward-statistics', { timezone }).pipe(
      map((response: ApiResponse<RewardStatistics>) => {
        if (response.status && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load reward statistics');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Get reward stats from attendant/rewards endpoint
   * @returns Observable of reward points record
   */
  getRewardStats(): Observable<RewardPointsRecord> {
    const timezone = getUserTimezone();
    return this.apiService.get<RewardsPointsApiResponse>('attendant/me/rewards', { page: 1, limit: 1, timezone }).pipe(
      map((response: ApiResponse<RewardsPointsApiResponse>) => {
        if (response.status && response.data?.data?.length) {
          return response.data.data[0];
        }
        throw new Error(response.message || 'Failed to load reward stats');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Get rewards summary for dashboard cards
   * Returns default summary based on reward settings
   * @returns Observable of rewards summary
   */
  getRewardsSummary(): Observable<RewardsSummary> {
    return this.getRewardSettings().pipe(
      map(settings => this.getDefaultSummary(settings)),
      tap(summary => this.summarySubject.next(summary)),
      catchError(error => {
        const defaultSummary = this.getDefaultSummary();
        this.summarySubject.next(defaultSummary);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get formatted rewards history for table display
   * @param filters - Filter options for history query
   * @returns Observable of history items and pagination
   */
  getRewardsHistory(filters: RewardsFilters = {}): Observable<{ history: RewardsHistoryItem[]; pagination: RewardsPagination }> {
    this.loadingSubject.next(true);
    const params = this.buildRewardsParams(filters);

    return this.apiService.get<RewardsPointsApiResponse>('attendant/me/rewards', params).pipe(
      map((response: ApiResponse<RewardsPointsApiResponse>) => {
        this.loadingSubject.next(false);
        
        if (!response.status || !response.data) {
          return this.getEmptyHistoryResponse();
        }

        const rewardsData = response.data.data ?? [];
        const pagination = response.data.pagination ?? this.getDefaultPagination();
        const history = this.formatRewardsPointsHistory(rewardsData);
        
        return { history, pagination };
      }),
      catchError(error => {
        this.loadingSubject.next(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current rewards data from subject
   * @returns Current rewards array
   */
  getCurrentRewards(): RewardRecord[] {
    return this.rewardsSubject.value;
  }

  /**
   * Get current summary from subject
   * @returns Current summary or null
   */
  getCurrentSummary(): RewardsSummary | null {
    return this.summarySubject.value;
  }

  /**
   * Check if data is currently loading
   * @returns Loading state
   */
  isLoading(): boolean {
    return this.loadingSubject.value;
  }

  /**
   * Clear all rewards data
   */
  clearRewardsData(): void {
    this.rewardsSubject.next([]);
    this.summarySubject.next(null);
  }

  /**
   * Get default pagination object
   * @returns Default pagination data
   */
  private getDefaultPagination(): RewardsPagination {
    return { total: 0, page: 1, limit: 10, totalPages: 0 };
  }

  /**
   * Get empty history response
   * @returns Empty history response with default pagination
   */
  private getEmptyHistoryResponse(): { history: RewardsHistoryItem[]; pagination: RewardsPagination } {
    return {
      history: [],
      pagination: this.getDefaultPagination()
    };
  }

  /**
   * Get default summary with optional settings
   * @param settings - Optional reward settings
   * @returns Default rewards summary
   */
  private getDefaultSummary(settings?: RewardSettings): RewardsSummary {
    const washCount = settings?.wash_count ?? 50;
    const thresholdReward = settings?.threshold_reward ?? 3;
    const membershipReward = settings?.membership_reward ?? 5;
    
    return {
      lifetimeRewards: 0,
      singleWashProgress: {
        current: 0,
        total: washCount,
        reward: `$${thresholdReward} for ${washCount} washes`,
        wonToday: '$0.00 won today'
      },
      membershipProgress: {
        reward: `$${membershipReward} for each membership`,
        description: 'Sell any membership.',
        wonToday: '$0.00 won today'
      }
    };
  }


  /**
   * Format rewards points API response to history items
   * Points are already dollar amounts from backend (grouping done server-side)
   * @param rewardsData - Array of reward points records
   * @returns Array of formatted history items
   */
  private formatRewardsPointsHistory(rewardsData: RewardPointsRecord[]): RewardsHistoryItem[] {
    if (!rewardsData?.length) {
      return [];
    }
    
    const shopTimezone = getUserTimezone();
    return rewardsData
      .filter((record) => !!record.date)
      .map((record) => {
        const singleWashPoints = parseFloat(record.single_wash_points ?? '0');
        const membershipPoints = parseFloat(record.membership_points ?? '0');
        const totalPoints = parseFloat(record.total_points ?? '0');
        // Keep calendar YYYY-MM-DD for lookups; template formats for display.
        // Resolve datetimes in the shop timezone (not browser-local) so rows stay on
        // the correct calendar day for staff viewing a shop in another timezone.
        return {
          date: this.resolveRecordCalendarDate(record.date!, shopTimezone),
          singleWashReward: `$${singleWashPoints.toFixed(2)}`,
          membershipReward: `$${membershipPoints.toFixed(2)}`,
          totalRewards: `$${totalPoints.toFixed(2)}`,
          membership_threshold_points: record.membership_threshold_points ?? '0'
        };
      });
  }

  /**
   * Calendar `YYYY-MM-DD` for a reward record date.
   * Plain `YYYY-MM-DD` is kept as-is; ISO datetimes resolve in the shop timezone.
   */
  private resolveRecordCalendarDate(date: string, shopTimezone: string): string {
    const head = String(date).trim().split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(head) && !String(date).includes('T')) {
      return head;
    }
    return formatCalendarDateInTimeZone(date, shopTimezone) || toLocalCalendarYmd(date);
  }

}
